import { Link, useRouterState } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { SethAvatar } from '../components/Avatar';
import { Logo } from '../components/Logo';
import { cx } from '../lib/cx';
import { meQuery } from '../lib/queries';
import { RAIL_NAV, useNavBadges, type NavItem } from './nav';
import { useLogout } from './useLogout';

function RailLink({ item, path, count }: { item: NavItem; path: string; count: number }) {
  const active = item.match(path);
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={item.to}
        preload="intent"
        aria-current={active ? 'page' : undefined}
        className={cx(
          'flex h-9 items-center gap-3 rounded-[10px] px-3 text-nav transition-colors duration-150',
          active ? 'bg-deep-light font-semibold text-white' : 'font-medium text-rail hover:bg-deep-light hover:text-white',
        )}
      >
        <Icon size={18} strokeWidth={1.8} className={active ? 'text-teal-light' : undefined} aria-hidden="true" />
        <span className="flex-1">{item.label}</span>
        {count > 0 ? (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-coral px-1.5 text-meta font-bold text-deep tabular">
            {count}
            <span className="sr-only">{item.badge === 'leads' ? ' waiting on you or new' : ' waiting'}</span>
          </span>
        ) : null}
      </Link>
    </li>
  );
}

/** Desktop left rail: wordmark, the places to go with a divider above Needs You, and Seth at the bottom. */
export function Rail() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useQuery(meQuery);
  const badges = useNavBadges();
  const logout = useLogout();

  return (
    <nav aria-label="Main" className="flex w-[208px] shrink-0 flex-col bg-deep px-3 pb-4 pt-6">
      <Logo width={132} className="ml-3" />
      <div className="mt-8 flex flex-col">
        {RAIL_NAV.map((group, i) => (
          <div key={i} className="contents">
            {i > 0 ? <span aria-hidden="true" className="mx-2.5 my-2 block h-px bg-rail/20" /> : null}
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {group.map((item) => (
                <RailLink key={item.to} item={item} path={path} count={item.badge ? badges[item.badge] : 0} />
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-auto flex items-center gap-2.5 rounded-[12px] bg-deep-light py-2.5 pl-2.5 pr-1.5">
        <SethAvatar size={30} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13px] font-semibold text-white">{me?.name ?? 'Seth'}</span>
          <span className="text-meta text-rail-dim">Owner</span>
        </span>
        <button
          type="button"
          onClick={() => logout.mutate()}
          aria-label="Log out"
          title="Log out"
          className="flex size-8 items-center justify-center rounded-full text-rail-dim transition-colors hover:bg-deep hover:text-white"
        >
          <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}
